import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  CustomizationAssetType,
  ICustomizationAsset,
  IUserCustomization,
} from '../../../../@core/interfaces/customization.interface';
import { IUser } from '../../../../@core/interfaces/user/user.interface';
import { SnackBarService } from '../../../../@core/services/snack-bar.service';
import { CustomizationService } from '../../../../@shared/services/customization.service';
import { UserService } from '../../../../@shared/services/user.service';

@Component({
  selector: 'app-user-customization',
  templateUrl: './user-customization.component.html',
  styleUrls: ['./user-customization.component.scss'],
})
export class UserCustomizationComponent implements OnInit, OnDestroy {
  readonly slots: Array<{ type: CustomizationAssetType; label: string }> = [
    { type: 'avatar', label: 'Аватар' },
    { type: 'banner', label: 'Баннер' },
    { type: 'frame', label: 'Рамка' },
  ];
  user: IUser | null = null;
  assets: ICustomizationAsset[] = [];
  state: IUserCustomization = { customization: [], granted: [] };
  selection: Record<CustomizationAssetType, number | null> = { avatar: null, banner: null, frame: null };
  grantAssetId: number | null = null;
  grantReason = '';
  loading = false;
  saving = false;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly customization: CustomizationService,
    private readonly userService: UserService,
    private readonly snackBar: SnackBarService,
  ) {}

  ngOnInit(): void {
    this.userService.user$.pipe(takeUntil(this.destroyed$)).subscribe(user => (this.user = user));
    this.load();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  load(): void {
    const userId = this.userService.currentUserId.getValue();
    this.loading = true;
    forkJoin({
      catalog: this.customization.getAssets({ pagination: { limit: 500, offset: 0 } }),
      state: this.customization.getUserCustomization(userId),
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: ({ catalog, state }) => {
          this.assets = catalog.assets;
          this.state = state;
          this.selection = { avatar: null, banner: null, frame: null };
          state.customization.forEach(asset => (this.selection[asset.type] = asset.id));
        },
        error: () => this.snackBar.openSnackBar('Не удалось загрузить кастомизацию пользователя', 3000, 'error'),
      });
  }

  assetsByType(type: CustomizationAssetType): ICustomizationAsset[] {
    return this.assets.filter(asset => asset.type === type);
  }

  selectedAsset(type: CustomizationAssetType): ICustomizationAsset | null {
    return this.assets.find(asset => asset.id === this.selection[type]) || null;
  }

  grantCandidates(): ICustomizationAsset[] {
    const grantedIds = new Set(this.state.granted.map(asset => asset.id));
    return this.assets.filter(asset => asset.requiresGrant && !grantedIds.has(asset.id));
  }

  save(): void {
    const userId = this.userService.currentUserId.getValue();
    const customization = this.slots.map(slot => ({ type: slot.type, assetId: this.selection[slot.type] }));
    this.saving = true;
    this.customization
      .setUserCustomization(userId, customization)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.snackBar.openSnackBar('Кастомизация пользователя сохранена', 2500, 'success');
          this.load();
        },
        error: () => this.snackBar.openSnackBar('Не удалось сохранить кастомизацию', 3000, 'error'),
      });
  }

  grant(): void {
    if (!this.grantAssetId) return;
    const userId = this.userService.currentUserId.getValue();
    this.customization.grantAsset(userId, this.grantAssetId, this.grantReason).subscribe({
      next: () => {
        this.grantAssetId = null;
        this.grantReason = '';
        this.snackBar.openSnackBar('Ассет выдан пользователю', 2500, 'success');
        this.load();
      },
      error: () => this.snackBar.openSnackBar('Не удалось выдать ассет', 3000, 'error'),
    });
  }

  revoke(asset: ICustomizationAsset): void {
    if (!window.confirm(`Отозвать «${asset.name}»? Применённое оформление останется выбранным.`)) return;
    this.customization.revokeAsset(this.userService.currentUserId.getValue(), asset.id).subscribe({
      next: () => {
        this.snackBar.openSnackBar('Ассет отозван', 2500, 'success');
        this.load();
      },
      error: () => this.snackBar.openSnackBar('Не удалось отозвать ассет', 3000, 'error'),
    });
  }
}
